
import { useEffect } from 'react';
import { PerfectTrackingProps } from './recycling/types';
import { useGlobalFlags } from './useGlobalFlags';

/**
 * Hook to manage perfect level and lesson tracking
 * This centralizes the logic for tracking whether a level was completed perfectly
 */
export const usePerfectTracking = ({ 
  selectedLesson,
  currentLevelIndex,
  currentLevelId,
  setIsPerfectLevel,
  handleImperfectLesson
}: PerfectTrackingProps) => {
  // Get global flags manager
  const { 
    markHadRecycledWords, 
    setPerfectLevelStatus,
    getHadRecycledWords
  } = useGlobalFlags({ currentLevelId });
  
  // Function to mark a level as imperfect (used by exercise components)
  const handleImperfectLevel = () => {
    console.debug('[usePerfectTracking] Marking level as imperfect');
    
    // Update React state - this is our primary state tracking mechanism
    setIsPerfectLevel(false);
    
    // Also update global flags to ensure consistent perfect tracking
    // These flags ensure the perfect status is correctly reflected in the UI
    setPerfectLevelStatus(false);
    markHadRecycledWords(currentLevelId);
  };
  
  // Function to determine the final perfect status considering all factors
  const determineFinalPerfectStatus = (currentPerfectState: boolean): boolean => {
    // Check the current React state
    const isPerfectFromReactState = currentPerfectState === true;
    
    // Check the global failed words flag
    const hadFailedWords = getHadRecycledWords();
    
    // Check if the match exercise was perfect - we need to prioritize the component's internal tracking
    const matchWasPerfect = (window as any).__MATCH_EXERCISE_IS_PERFECT__ === true;
    
    // FINAL PERFECT STATUS DETERMINATION
    // A level can ONLY be perfect if React state says it's perfect AND we never had failed words
    // OR if the match exercise explicitly reports it was perfect
    const wasPerfectLevel = (isPerfectFromReactState && !hadFailedWords) || matchWasPerfect;
    
    return wasPerfectLevel;
  };
  
  // Function to mark the lesson as imperfect when a level is imperfect
  const markLessonAsImperfect = () => {
    console.debug(`🏆 [usePerfectTracking] Level ${currentLevelIndex} (ID: ${currentLevelId}) was not perfect, marking lesson as imperfect`);
    
    // Call the handleImperfectLesson function from useLevelState
    if (handleImperfectLesson) {
      handleImperfectLesson();
    }
    
    // Also update global flag for immediate cross-component access
    (window as any).__PERFECT_LESSON__ = false;
    
    // Also update localStorage for lesson perfect status
    if (selectedLesson?.id) {
      try {
        localStorage.setItem(`lesson_${selectedLesson.id}_perfect`, 'false');
        console.debug(`🔐 [usePerfectTracking] Saved imperfect lesson status to localStorage for lesson ${selectedLesson.id}`);
      } catch (e) {
        console.error('Error setting localStorage:', e);
      }
    }
  };
  
  return {
    handleImperfectLevel,
    determineFinalPerfectStatus,
    markLessonAsImperfect
  };
};
